/**
 * Orders the estimate points: weight of the detector in the method (0–3) × strength ×
 * freshness. Weight 0 drops a point. Ties: judgement, weighs, supports. Seven at most.
 * The model never changes this order.
 */
import type {
  EstimateTemplateId,
  EstimateTriageCandidate,
  EstimateTriageDetectorId,
  EstimateTriageMethodView,
  EstimateTriageScoredCandidate,
} from '@popety-io/types'

type Weight = 0 | 1 | 2 | 3
type Weights = Record<EstimateTriageDetectorId, Weight>

const ORDER: EstimateTriageDetectorId[] = [
  'models',
  'final',
  'per_m2',
  'market',
  'registry',
  'comparables',
  'condition',
  'energy',
  'environment',
  'ownership',
  'parcel',
]
const w = (...ws: Weight[]): Weights =>
  Object.fromEntries(ORDER.map((d, i) => [d, ws[i]])) as Weights

/**
 * Template weights, in ORDER (24.10.2026). An agency defends a value before an owner; a
 * bank reads the value as collateral (price per m², energy, register); a property
 * manager reads the building (register, energy, condition, surroundings).
 */
export const ESTIMATE_TEMPLATE_WEIGHTS: Record<EstimateTemplateId, Weights> = {
  agence: w(3, 3, 2, 2, 3, 2, 2, 2, 1, 2, 1),
  banque: w(3, 3, 3, 2, 3, 2, 1, 3, 1, 1, 1),
  regie: w(2, 2, 2, 2, 3, 1, 2, 3, 2, 2, 1),
}

export const ESTIMATE_TRIAGE_DETECTORS = ORDER

const KIND_ORDER = { judgement: 0, weighs: 1, supports: 2 } as const

export const scoreEstimateCandidates = (
  cands: EstimateTriageCandidate[],
  method: EstimateTriageMethodView
): EstimateTriageScoredCandidate[] =>
  cands
    .map((c) => {
      const weight = method.weights[c.detector]
      return {
        ...c,
        weight,
        score: Math.round(weight * c.strength * c.freshness * 100) / 100,
      }
    })
    .filter((c) => c.weight > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        ORDER.indexOf(a.detector) - ORDER.indexOf(b.detector) ||
        a.id.localeCompare(b.id)
    )
    .slice(0, 7)
